import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen,
  ChevronRight,
  Cloud,
  Download,
  Home,
  GraduationCap,
  Plus,
  Upload,
  UserRound,
} from "lucide-react";
import { doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";

import { db } from "../../Security/firebase-config";
import { useAuth } from "../../context/AuthContext";
import { useTeam } from "../../context/TeamContext";
import useTeamQuotaStatus from "../../hooks/useTeamQuotaStatus";
import { STORAGE_ADD_ON_OPTIONS, SUBSCRIPTION_PLANS } from "../../config/subscriptionPlans";
import NotificationsDropdown from "../Notifications/NotificationsDropdown";
import CustomAlert from "./CustomAlert";
import SidebarSvgImage from "./SidebarSvgImage";
import { GUIDE_CATEGORY_CONTENT, GUIDE_MAPS } from "../../shared/guideCatalog";
import "./Sidebar.css";

const DEFAULT_COMING_SOON_MESSAGE = "This page is currently in development and coming soon.";
const THEME_EVENT = "valovate-theme-change";
const MOBILE_EXTRAS_KEY = "mobile-extras";
const SUBMENU_STORAGE_KEYS = {
  "agent-mastery": "sidebarAgentExpansions",
  "coordinated-play": "sidebarCoordinatedExpansions",
  "pro-player-analysis": "sidebarProExpansions",
};

function isDesktopCompanionApp() {
  if (typeof window === "undefined") return false;
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  return Boolean(window.__VALOVATE_DESKTOP_ACTIVE_TEAM_SUBSCRIPTION__) || /electron|valovate desktop/i.test(userAgent);
}

const ICONS = {
  managerOverview: "/assets/SVG-Icons/managers.svg",
  teamDashboard: "/assets/SVG-Icons/team_dashboard.svg",
  vodReview: "/assets/SVG-Icons/vod_review.svg",
  calendar: "/assets/SVG-Icons/calendar.svg",
  teamStats: "/assets/SVG-Icons/team_stats.svg",
  lfg: "/assets/SVG-Icons/LFG.svg",
  guideHub: "/assets/SVG-Icons/agent_mastery_guides.svg",
  guideLibrary: "/assets/SVG-Icons/guide_library.svg",
  agentMastery: "/assets/SVG-Icons/agent_mastery.svg",
  coordinatedPlay: "/assets/SVG-Icons/coordinated_play.svg",
  proPlayerAnalysis: "/assets/SVG-Icons/pro_player_analysis.svg",
  clips: "/assets/SVG-Icons/favorites.svg",
  feedback: "/assets/SVG-Icons/feedback.svg",
  about: "/assets/SVG-Icons/about.svg",
  pcAppDownload: "/assets/SVG-Icons/pc_app_download.svg",
  teamAvailability: "/assets/SVG-Icons/team_availability.svg",
  myAvailability: "/assets/SVG-Icons/event_personal.svg",
  settings: "/assets/SVG-Icons/team_tools.svg",
  moon: "/assets/SVG-Icons/moon.svg",
  sun: "/assets/SVG-Icons/sun.svg",
};

const SIDEBAR_ITEMS = [
  {
    key: "overview",
    label: "Home",
    to: "/home",
    icon: Home,
  },
  {
    key: "my-routine",
    label: "My Routine",
    to: "/my-routine",
    icon: UserRound,
  },
  {
    key: "team-center",
    label: "Team Center",
    to: "/team-center/dashboard",
    icon: ICONS.teamDashboard,
    panel: {
      variant: "links",
      items: [
        {
          key: "team-dashboard-home",
          label: "Team Dashboard",
          to: "/team-center/dashboard",
          icon: ICONS.teamDashboard,
        },
        {
          key: "team-settings",
          label: "Team Settings",
          to: "/team-center/settings",
          icon: ICONS.teamDashboard,
        },
      ],
    },
  },
  {key: "coach",label: "Coach",to: "/coach",icon: GraduationCap},
  {
    key: "manager-overview",
    label: "Manager",
    to: "/manager",
    icon: ICONS.managerOverview,
  },
  {
    key: "vod-review",
    label: "Team Library",
    to: "/library",
    icon: ICONS.vodReview,
  },
  {
    key: "guide-hub",
    label: "Guide Hub",
    to: "/guide-library",
    icon: ICONS.guideHub,
    panel: {
      variant: "categories",
      items: [
        {
          key: "strats-planner",
          label: "My Playbooks",
          icon: ICONS.guideLibrary,
          to: "/strats-planner",
        },
        {
          key: "guide-library",
          label: "Guide Library",
          icon: ICONS.guideLibrary,
          to: "/guide-library",
        },
        {
          key: "mvsk-basics",
          label: "MVSK Basics",
          icon: ICONS.guideLibrary,
          chapterGroups: GUIDE_MAPS.map((map) => ({
            key: map.toLowerCase(),
            title: map,
            links: GUIDE_CATEGORY_CONTENT.mvsk[map].map((item) => ({
              ...item,
              key: map.toLowerCase() + "-mvsk-coming-soon",
            })),
          })),
        },
        {
          key: "agent-mastery",
          label: "Agent Mastery",
          icon: ICONS.agentMastery,
          chapterGroups: [
            {
              key: "abyss",
              title: "Abyss",
              links: [
                { key: "abyss-map-macro", label: "Map Macro", disabled: true },
                { key: "abyss-kayo", label: "Kayo", disabled: true },
                { key: "abyss-sova", label: "Sova", disabled: true },
              ],
            },
            {
              key: "ascent",
              title: "Ascent",
              links: [
                { key: "ascent-map-macro", label: "Map Macro", disabled: true },
                { key: "ascent-kayo", label: "Kayo", disabled: true },
                { key: "ascent-sova", label: "Sova", disabled: true },
              ],
            },
            {
              key: "bind",
              title: "Bind",
              links: [
                { key: "bind-map-macro", label: "Map Macro", to: "/Bind-Map-Macro" },
                { key: "bind-gekko", label: "Gekko", disabled: true },
                { key: "bind-viper", label: "Viper", disabled: true },
              ],
            },
            {
              key: "breeze",
              title: "Breeze",
              links: [
                { key: "breeze-map-macro", label: "Map Macro", disabled: true },
                { key: "breeze-sova", label: "Sova", disabled: true },
                { key: "breeze-viper", label: "Viper", disabled: true },
              ],
            },
            {
              key: "corrode",
              title: "Corrode",
              links: [
                { key: "corrode-map-macro", label: "Map Macro", disabled: true },
                { key: "corrode-sova", label: "Sova", disabled: true },
                { key: "corrode-yoru", label: "Yoru", disabled: true },
              ],
            },
            {
              key: "fracture",
              title: "Fracture",
              links: [
                { key: "fracture-map-macro", label: "Map Macro", disabled: true },
                { key: "fracture-raze", label: "Raze", disabled: true },
                { key: "fracture-breach", label: "Breach", disabled: true },
              ],
            },
            {
              key: "haven",
              title: "Haven",
              links: [
                { key: "haven-map-macro", label: "Map Macro", disabled: true },
                { key: "haven-kj", label: "KJ", to: "/Haven-KJ" },
                { key: "haven-sova", label: "Sova", disabled: true },
              ],
            },
            {
              key: "icebox",
              title: "Icebox",
              links: [
                { key: "icebox-map-macro", label: "Map Macro", disabled: true },
                { key: "icebox-kayo", label: "Kayo", disabled: true },
                { key: "icebox-sova", label: "Sova", disabled: true },
              ],
            },
            {
              key: "lotus",
              title: "Lotus",
              links: [
                { key: "lotus-map-macro", label: "Map Macro", disabled: true },
                { key: "lotus-fade", label: "Fade", disabled: true },
                { key: "lotus-viper", label: "Viper", disabled: true },
              ],
            },
            {
              key: "pearl",
              title: "Pearl",
              links: [
                { key: "pearl-map-macro", label: "Map Macro", disabled: true },
                { key: "pearl-fade", label: "Fade", disabled: true },
                { key: "pearl-kj", label: "KJ", disabled: true },
              ],
            },
            {
              key: "split",
              title: "Split",
              links: [
                { key: "split-map-macro", label: "Map Macro", to: "/Split-Map-Macro" },
                { key: "split-skye", label: "Skye", disabled: true },
                { key: "split-viper", label: "Viper", disabled: true },
              ],
            },
            {
              key: "summit",
              title: "Summit",
              links: [
                { key: "summit-map-macro", label: "Map Macro", disabled: true },
                { key: "summit-slot-1", label: "Coming Soon", disabled: true },
                { key: "summit-slot-2", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "sunset",
              title: "Sunset",
              links: [
                { key: "sunset-map-macro", label: "Map Macro", disabled: true },
                { key: "sunset-cypher", label: "Cypher", disabled: true },
                { key: "sunset-sova", label: "Sova", disabled: true },
              ],
            },
          ],
        },
        {
          key: "coordinated-play",
          label: "Coordinated Play",
          icon: ICONS.coordinatedPlay,
          chapterGroups: [
            {
              key: "abyss-coord",
              title: "Abyss",
              links: [
                { key: "abyss-coord-1", label: "Coming Soon", disabled: true },
                { key: "abyss-coord-2", label: "Coming Soon", disabled: true },
                { key: "abyss-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "ascent-coord",
              title: "Ascent",
              links: [
                { key: "ascent-coord-1", label: "Coming Soon", disabled: true },
                { key: "ascent-coord-2", label: "Coming Soon", disabled: true },
                { key: "ascent-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "bind-coord",
              title: "Bind",
              links: [
                {
                  key: "bind-nrg-2026",
                  label: "NRG 2026 Bind Comp",
                  to: "/playbook/NRG2026BindComp",
                },
                { key: "bind-coord-2", label: "Coming Soon", disabled: true },
                { key: "bind-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "breeze-coord",
              title: "Breeze",
              links: [
                { key: "breeze-coord-1", label: "Coming Soon", disabled: true },
                { key: "breeze-coord-2", label: "Coming Soon", disabled: true },
                { key: "breeze-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "corrode-coord",
              title: "Corrode",
              links: [
                { key: "corrode-coord-1", label: "Coming Soon", disabled: true },
                { key: "corrode-coord-2", label: "Coming Soon", disabled: true },
                { key: "corrode-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "fracture-coord",
              title: "Fracture",
              links: [
                { key: "fracture-coord-1", label: "Coming Soon", disabled: true },
                { key: "fracture-coord-2", label: "Coming Soon", disabled: true },
                { key: "fracture-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "haven-coord",
              title: "Haven",
              links: [
                { key: "haven-coord-1", label: "Coming Soon", disabled: true },
                { key: "haven-coord-2", label: "Coming Soon", disabled: true },
                { key: "haven-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "icebox-coord",
              title: "Icebox",
              links: [
                { key: "icebox-coord-1", label: "Coming Soon", disabled: true },
                { key: "icebox-coord-2", label: "Coming Soon", disabled: true },
                { key: "icebox-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "lotus-coord",
              title: "Lotus",
              links: [
                { key: "lotus-coord-1", label: "Coming Soon", disabled: true },
                { key: "lotus-coord-2", label: "Coming Soon", disabled: true },
                { key: "lotus-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "pearl-coord",
              title: "Pearl",
              links: [
                { key: "pearl-coord-1", label: "Coming Soon", disabled: true },
                { key: "pearl-coord-2", label: "Coming Soon", disabled: true },
                { key: "pearl-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "split-coord",
              title: "Split",
              links: [
                {
                  key: "split-sen-comp",
                  label: "Split SEN Comp",
                  to: "/playbook/Split-SEN-Comp",
                },
                { key: "split-coord-2", label: "Coming Soon", disabled: true },
                { key: "split-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "summit-coord",
              title: "Summit",
              links: [
                { key: "summit-coord-1", label: "Coming Soon", disabled: true },
                { key: "summit-coord-2", label: "Coming Soon", disabled: true },
                { key: "summit-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
            {
              key: "sunset-coord",
              title: "Sunset",
              links: [
                { key: "sunset-coord-1", label: "Coming Soon", disabled: true },
                { key: "sunset-coord-2", label: "Coming Soon", disabled: true },
                { key: "sunset-coord-3", label: "Coming Soon", disabled: true },
              ],
            },
          ],
        },
        {
          key: "pro-player-analysis",
          label: "Pro-Player Analysis",
          icon: ICONS.proPlayerAnalysis,
          chapterGroups: [
            { key: "abyss-pro", title: "Abyss", links: [{ key: "abyss-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "ascent-pro", title: "Ascent", links: [{ key: "ascent-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "bind-pro", title: "Bind", links: [{ key: "bind-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "breeze-pro", title: "Breeze", links: [{ key: "breeze-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "corrode-pro", title: "Corrode", links: [{ key: "corrode-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "fracture-pro", title: "Fracture", links: [{ key: "fracture-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "haven-pro", title: "Haven", links: [{ key: "haven-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "icebox-pro", title: "Icebox", links: [{ key: "icebox-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "lotus-pro", title: "Lotus", links: [{ key: "lotus-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "pearl-pro", title: "Pearl", links: [{ key: "pearl-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "split-pro", title: "Split", links: [{ key: "split-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "summit-pro", title: "Summit", links: [{ key: "summit-pro-1", label: "Coming Soon", disabled: true }] },
            { key: "sunset-pro", title: "Sunset", links: [{ key: "sunset-pro-1", label: "Coming Soon", disabled: true }] },
          ],
        },
      ],
    },
  },
  {
    key: "lfg",
    label: "LFG",
    to: "/lfg",
    icon: ICONS.lfg,
  },
  {
    key: "clips",
    label: "Clips",
    to: "/clips",
    icon: ICONS.clips,
  },
  {
    key: "feedback",
    label: "Feedback",
    to: "/feedback",
    icon: ICONS.feedback,
  },
  {
    key: "about-us",
    label: "About Us",
    to: "/#about",
    icon: ICONS.about,
  },
  {
    key: "pc-app-download",
    label: "Desktop App",
    to: "/desktop-app/download",
    icon: ICONS.pcAppDownload,
  },
];

const SIDEBAR_UTILITY_KEYS = new Set(["feedback", "about-us", "pc-app-download"]);
const SIDEBAR_UTILITY_ITEMS = SIDEBAR_ITEMS.filter((item) => SIDEBAR_UTILITY_KEYS.has(item.key));

const GUIDE_ACTIVE_PATHS = new Set([
  "/guide-library",
  "/Bind-Map-Macro",
  "/Bind-Gekko",
  "/Split-Map-Macro",
  "/Haven-KJ",
  "/Corrode-Yoru",
]);

function clampPercent(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

function safeToMillis(value) {
  if (!value) return null;

  if (typeof value === "object" && value !== null && typeof value.toDate === "function") {
    const date = value.toDate();
    return Number.isFinite(date.getTime()) ? date.getTime() : null;
  }

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value.getTime() : null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed.getTime() : null;
  }

  return null;
}

function formatMonthDay(value) {
  const ms = safeToMillis(value);
  if (!ms) return "";

  return new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "numeric",
  }).format(new Date(ms));
}

function formatPlanLabel(value) {
  if (typeof value !== "string" || !value.trim()) {
    return "No active plan";
  }

  const normalized = value.trim().toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function formatStatusLabel(value) {
  if (typeof value !== "string" || !value.trim()) {
    return "Status unknown";
  }

  return value
    .trim()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function formatGbValue(bytes) {
  const gb = Number(bytes || 0) / 1024 ** 3;
  return Number.isFinite(gb) ? gb.toFixed(gb >= 100 ? 0 : gb >= 10 ? 1 : 2) : "0.00";
}

function getUsageTone(percent) {
  if (percent >= 90) return "danger";
  if (percent >= 75) return "warning";
  return "normal";
}

function getUsageColor(percent) {
  const tone = getUsageTone(percent);
  if (tone === "danger") return "var(--clrfd4556)";
  if (tone === "warning") return "#f3c04d";
  return "var(--clr22bbd7)";
}

function bytesFromGb(value) {
  const gb = Number(value || 0);
  return Number.isFinite(gb) && gb > 0 ? gb * 1024 ** 3 : 0;
}

function getPlanQuotaFallback(planKey, storageAddOnKey) {
  const normalizedPlanKey =
    typeof planKey === "string" && planKey.trim() ? planKey.trim().toLowerCase() : "";
  const plan = SUBSCRIPTION_PLANS?.[normalizedPlanKey] || null;
  if (!plan) return null;

  const selectedStorageAddOn = STORAGE_ADD_ON_OPTIONS.find((option) => option.key === storageAddOnKey) || null;
  const baseStorageBytes = bytesFromGb(plan.storageGb);
  const storageAddOnBytes = bytesFromGb(selectedStorageAddOn?.totalStorageGb || 0);

  return {
    storageLimitBytes: Math.max(baseStorageBytes, storageAddOnBytes),
    monthlyUploadLimitBytes: bytesFromGb(plan.monthlyUploadGb),
  };
}

function applyTheme(theme) {
  if (theme !== "light" && theme !== "dark") return;

  document.documentElement.classList.remove("light", "dark");
  document.body.classList.remove("light", "dark");
  document.documentElement.classList.add(theme);
  document.body.classList.add(theme);
}

function broadcastTheme(theme) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { theme } }));
}

function isGuideRoute(pathname) {
  return pathname === "/guides" || pathname.startsWith("/guides/") || pathname.startsWith("/guide-admin") || GUIDE_ACTIVE_PATHS.has(pathname) || pathname === "/strats-planner" || pathname.startsWith("/strats-planner/") || pathname.startsWith("/playbook/");
}

function matchesLinkTarget(to, pathname, hash) {
  if (typeof to !== "string" || !to.trim()) {
    return false;
  }

  const [targetPath, targetHash = ""] = to.split("#");
  if (targetHash) {
    return pathname === targetPath && hash === `#${targetHash}`;
  }

  return pathname === to;
}

function isItemActive(item, pathname, hash, search = "") {
  switch (item.key) {
    case "overview":
      return ["/home","/overview"].includes(pathname) && !hash;
    case "team-center":
      return (
        pathname === "/team-center" ||
        pathname.startsWith("/team-center/") ||
        pathname === "/team-dashboard" ||
        pathname.startsWith("/team-dashboard/") ||
        pathname === "/team-setup-billing" ||
        pathname === "/team-setup-payers" ||
        matchesLinkTarget(item.to, pathname, hash)
      );
    case "calendar":
      return pathname === "/calendar" || pathname === "/calendar/availability" || pathname === "/calendar/my-availability";
    case "team-stats":
      return pathname === "/team-stats" || pathname === "/team-stats/studio" || pathname === "/team-stats/summary";
    case "vod-review":
      return pathname==='/library'||pathname==='/review'||pathname.startsWith('/review/')||pathname.startsWith('/team-stats');
    case "guide-hub":
      return isGuideRoute(pathname) || matchesLinkTarget(item.to, pathname, hash);
    case "my-routine":
      return pathname === "/my-routine" || pathname === "/my-prep";
    default:
      return matchesLinkTarget(item.to, pathname, hash);
  }
}

function isAuthRoute(pathname) {
  return (
    pathname === "/login" ||
    pathname === "/signup" ||
    pathname === "/account" ||
    pathname === "/pick-username" ||
    pathname === "/verify" ||
    pathname === "/verify-email"
  );
}

function SidebarIcon({ icon }) {
  const isImageIcon = typeof icon === "string" && icon.trim().length > 0;
  const Icon = isImageIcon ? null : icon;

  return (
    <span className="New-Sidebar__iconSlot" aria-hidden="true">
      {isImageIcon ? (
        <SidebarSvgImage src={icon} className="New-Sidebar__svgIcon" loading="eager" decoding="async" />
      ) : (
        <Icon size={47} strokeWidth={2.15} />
      )}
    </span>
  );
}

function SidebarUsageCard({ label, icon, usedBytes, limitBytes, resetLabel, loading, compact = false, wide = false, description }) {
  const hasLimit = Number(limitBytes || 0) > 0;
  const percent = hasLimit ? clampPercent((Number(usedBytes || 0) / Number(limitBytes || 0)) * 100) : 0;
  const isImageIcon = typeof icon === "string" && icon.trim().length > 0;
  const Icon = isImageIcon ? null : icon;
  const ringStyle = {
    "--nsb-usage-percent": `${percent}%`,
    "--nsb-usage-color": getUsageColor(percent),
  };
  const usageText = loading ? "Loading" : hasLimit
    ? `${formatGbValue(usedBytes)} of ${formatGbValue(limitBytes)} GB used`
    : "No quota available";
  const usageTitle = `${label}: ${loading ? "Loading" : hasLimit ? `${formatGbValue(usedBytes)} / ${formatGbValue(limitBytes)} GB` : "No quota available"}${resetLabel ? ` · Resets ${resetLabel}` : ""}${description ? `. ${description}` : ""}`;

  return (
    <article className={`New-Sidebar__usageCard${compact ? " New-Sidebar__usageCard--compact" : ""}${wide ? " New-Sidebar__usageCard--wide" : ""}`} aria-label={`${label}: ${usageText}`} title={usageTitle}>
      <span className="New-Sidebar__usageEyebrow">{label}</span>
      <div className="New-Sidebar__usageValue" aria-live="polite">
        {loading ? (
          <strong>Loading...</strong>
        ) : hasLimit ? (
          <>
            <strong>{formatGbValue(usedBytes)}</strong>
            <span>/ {formatGbValue(limitBytes)} GB</span>
          </>
        ) : (
          <strong>-- / --</strong>
        )}
      </div>
      <div className="New-Sidebar__usageRing" style={ringStyle} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={hasLimit && !loading ? percent : undefined} aria-valuetext={usageText}>
        <div className="New-Sidebar__usageRingCore">
          <span className="New-Sidebar__usageRingIcon" aria-hidden="true">
            {isImageIcon ? (
              <SidebarSvgImage src={icon} className="New-Sidebar__svgIcon New-Sidebar__svgIcon--usage" loading="eager" decoding="async" />
            ) : (
              <Icon size={40} strokeWidth={2.2} />
            )}
          </span>
        </div>
      </div>
      <span className="New-Sidebar__usageTrack" aria-hidden="true"><span style={{width:`${percent}%`,background:getUsageColor(percent)}}/></span>
      <div className="New-Sidebar__usageMeta">
        {resetLabel ? <small>Resets {resetLabel}</small> : <small>&nbsp;</small>}
      </div>
    </article>
  );
}

function SidebarTransferUsage({ quotaState, resetLabel, compact = false }) {
  const userQuota = quotaState.userQuota;
  if (!(Number(userQuota?.egressLimitBytes || 0) > 0)) return null;

  return (
    <SidebarUsageCard
      compact={compact}
      wide={!compact}
      label="Your monthly transfer"
      icon={Download}
      usedBytes={Number(userQuota.egressUsedBytes || 0)}
      limitBytes={Number(userQuota.egressLimitBytes)}
      resetLabel={formatMonthDay(userQuota.currentPeriodEnd) || resetLabel}
      description="Your allowance in this team. Counted uploads, downloads and VOD playback use this same per-member allowance."
    />
  );
}

function SidebarSummaryCard({
  activeTeamId,
  authLoading,
  userData,
  teamOptions,
  handleTeamSwitch,
  teamSwitching = false,
  closePanels,
  subscriptionPlanLabel,
  subscriptionStatusLabel,
  uploadUsed,
  uploadLimit,
  uploadResetLabel,
  storageUsed,
  storageLimit,
  quotaState,
  showUsage = true,
}) {
  return (
    <div className="New-Sidebar__summaryBox">
      <div className="New-Sidebar__teamRow">
        <div className="New-Sidebar__teamSelectWrap">
          <span className="New-Sidebar__eyebrow">Team Selection</span>
          <select
            className="New-Sidebar__teamSelect"
            value={activeTeamId || ""}
            onChange={handleTeamSwitch}
            disabled={authLoading || teamSwitching || !userData?.uid || teamOptions.length === 0}
            aria-label="Select active team"
          >
            {!userData?.uid ? <option value="">Sign in to load teams</option> : null}
            {userData?.uid && !activeTeamId && teamOptions.length > 0 ? <option value="">Select a team</option> : null}
            {userData?.uid && teamOptions.length === 0 ? <option value="">No teams available</option> : null}
            {teamOptions.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>

        <Link
          to="/team-center/settings?openCreateTeam=1"
          className="New-Sidebar__createTeam"
          aria-label="Create team"
          title="Create team"
          onClick={closePanels}
        >
          <Plus size={38} strokeWidth={2.4} />
        </Link>
      </div>

      <article className="New-Sidebar__subscriptionCard">
        <span className="New-Sidebar__eyebrow">Team Subscription</span>
        <div className="New-Sidebar__subscriptionRow">
          <strong>{subscriptionPlanLabel}</strong>
          <span className="New-Sidebar__statusPill">{subscriptionStatusLabel}</span>
        </div>
      </article>

      {showUsage ? (
        <div className="New-Sidebar__usageGrid">
          <SidebarUsageCard
            label="Upload Limit"
            icon={Upload}
            usedBytes={uploadUsed}
            limitBytes={uploadLimit}
            resetLabel={uploadResetLabel}
            loading={quotaState.loading && uploadLimit <= 0}
          />
          <SidebarUsageCard
            label="Cloud Storage"
            icon={Cloud}
            usedBytes={storageUsed}
            limitBytes={storageLimit}
            loading={quotaState.loading && storageLimit <= 0}
          />
          <SidebarTransferUsage quotaState={quotaState} resetLabel={uploadResetLabel} />
        </div>
      ) : null}
    </div>
  );
}

function SidebarThemeToggle({ theme, onToggle }) {
  const nextTheme = theme === "dark" ? "light" : "dark";

  return (
    <button
      type="button"
      className={`New-Sidebar__themeRow ${theme === "light" ? "is-light" : ""}`}
      onClick={onToggle}
      aria-label={`Switch to ${nextTheme} theme`}
    >
      <SidebarIcon icon={theme === "dark" ? ICONS.moon : ICONS.sun} />
      <span className="New-Sidebar__themeLabel">Theme</span>

      <span className="New-Sidebar__themeSwitch" aria-hidden="true">
        <span className="New-Sidebar__themeSlot">
          <span className="New-Sidebar__themeMoon" aria-hidden="true">
            <SidebarSvgImage src={ICONS.moon} className="New-Sidebar__themeIcon" loading="eager" decoding="async" />
          </span>
          <span className="New-Sidebar__themeThumb" aria-hidden="true" />
          <span className="New-Sidebar__themeSun" aria-hidden="true">
            <SidebarSvgImage src={ICONS.sun} className="New-Sidebar__themeIcon New-Sidebar__themeIcon--sun" loading="eager" decoding="async" />
          </span>
        </span>
      </span>
    </button>
  );
}

function SidebarProfileLink({
  to,
  settingsTo = to,
  profileInitial,
  displayName,
  onNavigate,
  showSettingsIcon = true,
}) {
  return (
    <div className={`New-Sidebar__profileRow ${showSettingsIcon ? "" : "New-Sidebar__profileRow--withoutSettings"}`}>
      <Link to={to} className="New-Sidebar__profileAccount" onClick={onNavigate}>
        <span className="New-Sidebar__profileInitial" aria-hidden="true">
          {profileInitial}
        </span>
        <span className="New-Sidebar__profileName">{displayName}</span>
      </Link>
      {showSettingsIcon ? (
        <Link
          to={settingsTo}
          className="New-Sidebar__profileSettings"
          aria-label="Settings"
          title="Settings"
          onClick={onNavigate}
        >
          <SidebarSvgImage src={ICONS.settings} className="New-Sidebar__svgIcon" loading="eager" decoding="async" />
        </Link>
      ) : null}
    </div>
  );
}

function SidebarUtilityDock({ closePanels, userData, profileInitial, displayName }) {
  const accountPath = userData?.uid ? "/account" : "/login";
  const settingsPath = userData?.uid ? "/account#settings" : "/login";

  return (
    <div className="New-Sidebar__utilityArea">
      <div className="New-Sidebar__utilityDock" role="group" aria-label="Sidebar utilities">
        {SIDEBAR_UTILITY_ITEMS.map((item) => (
          <Link
            key={item.key}
            to={item.to}
            className={`New-Sidebar__utilityButton ${
              item.key === "about-us" ? "New-Sidebar__utilityButton--about" : ""
            }`}
            aria-label={item.label}
            title={item.label}
            onClick={closePanels}
          >
            <SidebarIcon icon={item.icon} />
          </Link>
        ))}

        <div className="New-Sidebar__utilityProfile">
          <SidebarProfileLink
            to={accountPath}
            settingsTo={settingsPath}
            profileInitial={profileInitial}
            displayName={displayName}
            onNavigate={closePanels}
          />
        </div>
      </div>
    </div>
  );
}

function SidebarFooterControls({
  theme,
  toggleTheme,
  closePanels,
  userData,
  profileInitial,
  displayName,
  notificationTriggerMode,
  notificationDropdownClassName,
  showProfile = true,
  compact = false,
}) {
  return (
    <>
      <div className={`New-Sidebar__footerQuickControls ${compact ? "is-compact" : ""}`}>
        <NotificationsDropdown
          triggerMode={notificationTriggerMode}
          label="Notifications"
          containerClassName="New-Sidebar__footerAction New-Sidebar__footerAction--notifications"
          buttonClassName="New-Sidebar__notificationButton"
          imageClassName="New-Sidebar__notificationIconImage"
          labelClassName="New-Sidebar__footerLabel"
          dropdownClassName={notificationDropdownClassName}
        />

        <SidebarThemeToggle theme={theme} onToggle={toggleTheme} />
      </div>

      {showProfile ? (
        <SidebarProfileLink
          to={userData?.uid ? "/account" : "/login"}
          settingsTo={userData?.uid ? "/account#settings" : "/login"}
          profileInitial={profileInitial}
          displayName={displayName}
          onNavigate={closePanels}
        />
      ) : null}
    </>
  );
}

function SidebarLinkAction({ item, className, onNavigate, onDisabled }) {
  if (item.disabled) {
    return (
      <button
        type="button"
        className={`${className} is-disabled`}
        onClick={() => onDisabled(item.notice || DEFAULT_COMING_SOON_MESSAGE)}
      >
        <span className="New-Sidebar__panelLinkLabel">
          {item.icon ? <SidebarIcon icon={item.icon} /> : null}
          <span>{item.label}</span>
        </span>
      </button>
    );
  }

  return (
    <Link className={className} to={item.to} onClick={onNavigate}>
      <span className="New-Sidebar__panelLinkLabel">
        {item.icon ? <SidebarIcon icon={item.icon} /> : null}
        <span>{item.label}</span>
      </span>
    </Link>
  );
}

function SidebarMobileAuthAction({ isLoggedIn, isActive, onNavigate }) {
  return (
    <li data-key="mobile-auth" className="New-Sidebar__item New-Sidebar__item--mobileUtility">
      <Link
        to={isLoggedIn ? "/account" : "/login"}
        className={`New-Sidebar__trigger ${isActive ? "is-active" : ""}`}
        onClick={onNavigate}
        aria-label={isLoggedIn ? "Open account page" : "Open login page"}
      >
        <SidebarIcon icon={UserRound} />
        <span className="New-Sidebar__triggerLabel">{isLoggedIn ? "Account" : "Login"}</span>
      </Link>
    </li>
  );
}

function buildDefaultExpansions(groups) {
  return groups.reduce((accumulator, group) => {
    accumulator[group.key] = true;
    return accumulator;
  }, {});
}

function loadGroupExpansions(storageKey, groups) {
  const defaults = buildDefaultExpansions(groups);

  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return defaults;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return defaults;
    }

    return groups.reduce((accumulator, group) => {
      accumulator[group.key] =
        typeof parsed[group.key] === "boolean" ? parsed[group.key] : defaults[group.key];
      return accumulator;
    }, {});
  } catch {
    return defaults;
  }
}

function SidebarGroupedMenu({ sectionKey, groups, onNavigate, onDisabled }) {
  const storageKey = SUBMENU_STORAGE_KEYS[sectionKey] || `sidebarGroupExpansions:${sectionKey}`;
  const [expansions, setExpansions] = useState(() => loadGroupExpansions(storageKey, groups));

  useEffect(() => {
    setExpansions((current) => {
      const next = groups.reduce((accumulator, group) => {
        accumulator[group.key] =
          typeof current[group.key] === "boolean" ? current[group.key] : true;
        return accumulator;
      }, {});

      const currentKeys = Object.keys(current);
      const nextKeys = Object.keys(next);
      const isSame =
        currentKeys.length === nextKeys.length &&
        nextKeys.every((key) => current[key] === next[key]);

      return isSame ? current : next;
    });
  }, [groups]);

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(expansions));
  }, [expansions, storageKey]);

  const toggleGroup = (groupKey) => {
    setExpansions((current) => ({
      ...current,
      [groupKey]: !current[groupKey],
    }));
  };

  return (
    <div className="New-Sidebar__submenuGrid" role="menu">
      {groups.map((group) => (
        <section key={group.key} className="New-Sidebar__submenuCol">
          <div className="New-Sidebar__submenuHeadingRow">
            <h4 className="New-Sidebar__submenuHeading">{group.title}</h4>
            <button
              type="button"
              className="New-Sidebar__submenuToggle"
              aria-label={`${expansions[group.key] ? "Collapse" : "Expand"} ${group.title}`}
              aria-expanded={!!expansions[group.key]}
              onClick={() => toggleGroup(group.key)}
              title={expansions[group.key] ? "Collapse" : "Expand"}
            >
              <span className="New-Sidebar__submenuToggleIcon" aria-hidden="true">
                {expansions[group.key] ? "-" : "+"}
              </span>
            </button>
          </div>

          {expansions[group.key] ? (
            <div className="New-Sidebar__submenuLinks">
              {group.links.map((link) => (
                <SidebarLinkAction
                  key={link.key}
                  item={link}
                  className="New-Sidebar__submenuLink"
                  onNavigate={onNavigate}
                  onDisabled={onDisabled}
                />
              ))}
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
}

function SidebarCategoryMenu({ item, isMobile, onNavigate, onDisabled }) {
  const categoryIcon = item.icon || BookOpen;

  return (
    <div className="New-Sidebar__panelEntry New-Sidebar__panelEntry--nested">
      <button type="button" className="New-Sidebar__panelLink New-Sidebar__panelLink--nestedTrigger">
        <span className="New-Sidebar__panelLinkLabel">
          <SidebarIcon icon={categoryIcon} />
          <span>{item.label}</span>
        </span>
        <ChevronRight className="New-Sidebar__panelChevron" size={18} aria-hidden="true" />
      </button>

      <div className="New-Sidebar__nestedPanel">
        <SidebarGroupedMenu
          sectionKey={item.key}
          groups={Array.isArray(item.chapterGroups) ? item.chapterGroups : item.groups}
          onNavigate={onNavigate}
          onDisabled={onDisabled}
        />

        {isMobile ? <div className="New-Sidebar__nestedPanelSpacer" aria-hidden="true" /> : null}
      </div>
    </div>
  );
}

function SidebarDropdownPanel({ panel, isMobile, onNavigate, onDisabled }) {
  if (panel.variant === "categories") {
    return (
      <div className="New-Sidebar__panelBody New-Sidebar__panelBody--categories">
        {panel.items.map((item) => {
          const hasNestedContent = Array.isArray(item.chapterGroups) || Array.isArray(item.groups);
          if (!hasNestedContent) {
            return (
              <SidebarLinkAction
                key={item.key}
                item={item}
                className="New-Sidebar__panelLink"
                onNavigate={onNavigate}
                onDisabled={onDisabled}
              />
            );
          }

          return (
            <SidebarCategoryMenu
              key={item.key}
              item={item}
              isMobile={isMobile}
              onNavigate={onNavigate}
              onDisabled={onDisabled}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="New-Sidebar__panelBody">
      {panel.items.map((item) => (
        <SidebarLinkAction
          key={item.key}
          item={item}
          className="New-Sidebar__panelLink"
          onNavigate={onNavigate}
          onDisabled={onDisabled}
        />
      ))}
    </div>
  );
}

export default function Sidebar() {
  const navRef = useRef(null);
  const desktopPanelRef = useRef(null);
  const triggerRefs = useRef({});
  const closeTimerRef = useRef(null);
  const { userData, loading: authLoading } = useAuth();
  const { teamId: activeTeamId, setTeamId, username } = useTeam();
  const quotaState = useTeamQuotaStatus(activeTeamId);
  const showQuotaUsage = !isDesktopCompanionApp();
  const location = useLocation();
  const navigate=useNavigate();

  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);
  const [teamSwitching, setTeamSwitching] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [alertState, setAlertState] = useState(null);
  const [desktopPanelPosition, setDesktopPanelPosition] = useState(null);
  const [suppressHoverExpand, setSuppressHoverExpand] = useState(false);
  const [theme, setTheme] = useState(() => {
    const storedTheme = localStorage.getItem("theme");
    return storedTheme === "light" || storedTheme === "dark" ? storedTheme : "dark";
  });
  const activeDesktopPanelItem = useMemo(() => {
    if (isMobile || !openDropdown) {
      return null;
    }

    return SIDEBAR_ITEMS.find((item) => item.key === openDropdown && item.panel) || null;
  }, [isMobile, openDropdown]);

  useEffect(() => {
    // Match Sidebar.css exactly. Touch support also exists on desktop browsers;
    // using it as a layout test disables hover panels inside the desktop layout.
    const mobileLayout = window.matchMedia("(max-width: 46rem)");
    const handleViewportChange = () => {
      setIsMobile(mobileLayout.matches);
    };

    handleViewportChange();
    mobileLayout.addEventListener("change", handleViewportChange);

    return () => {
      mobileLayout.removeEventListener("change", handleViewportChange);
    };
  }, []);

  useEffect(() => {
    if (!userData?.uid) {
      setTeamOptions([]);
      return undefined;
    }

    let cancelled = false;
    const userRef = doc(db, "users", userData.uid);

    const stop = onSnapshot(
      userRef,
      async (snapshot) => {
        if (!snapshot.exists()) {
          if (!cancelled) {
            setTeamOptions([]);
          }
          return;
        }

        const data = snapshot.data() || {};
        const teamIds = Array.isArray(data.teamIds) ? data.teamIds.filter(Boolean) : [];

        const resolvedTeams = await Promise.all(
          teamIds.map(async (teamId) => {
            try {
              const teamSnapshot = await getDoc(doc(db, "teams", teamId));
              if (!teamSnapshot.exists()) {
                return null;
              }

              const teamData = teamSnapshot.data() || {};
              return {
                id: teamId,
                name: teamData.teamName || teamData.name || "Unnamed Team",
              };
            } catch {
              return null;
            }
          }),
        );

        if (!cancelled) {
          setTeamOptions(resolvedTeams.filter(Boolean));
        }
      },
      () => {
        if (!cancelled) {
          setTeamOptions([]);
        }
      },
    );

    return () => {
      cancelled = true;
      stop();
    };
  }, [userData?.uid]);

  useEffect(() => {
    if (!activeTeamId) {
      setSubscription(null);
      return undefined;
    }

    const subscriptionRef = doc(db, "teams", activeTeamId, "billingV2", "subscription");

    return onSnapshot(
      subscriptionRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setSubscription(null);
          return;
        }

        const data = snapshot.data() || {};
        setSubscription({
          planKey:
            typeof data.planKey === "string" && data.planKey.trim()
              ? data.planKey.trim().toLowerCase()
              : null,
          status:
            typeof data.status === "string" && data.status.trim()
              ? data.status.trim().toLowerCase()
              : null,
          selectedStorageAddOnKey:
            typeof data.selectedStorageAddOnKey === "string" && data.selectedStorageAddOnKey.trim()
              ? data.selectedStorageAddOnKey.trim()
              : null,
          currentPeriodEnd: data.currentPeriodEnd || null,
          cancelAtPeriodEnd: data.cancelAtPeriodEnd === true,
        });
      },
      () => {
        setSubscription(null);
      },
    );
  }, [activeTeamId]);

  useEffect(() => {
    const handleDocPointer = (event) => {
      if (!navRef.current) {
        return;
      }

      if (!navRef.current.contains(event.target)) {
        setOpenDropdown(null);
        setSuppressHoverExpand(false);
      }
    };

    document.addEventListener("pointerdown", handleDocPointer);
    return () => document.removeEventListener("pointerdown", handleDocPointer);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
  }, [isMobile, location.pathname, location.hash]);

  useEffect(() => {
    if (isMobile) {
      setSuppressHoverExpand(false);
    }
  }, [isMobile]);

  useEffect(() => () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!alertState) return undefined;

    const timer = window.setTimeout(() => setAlertState(null), 3200);
    return () => window.clearTimeout(timer);
  }, [alertState]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === "theme" && (event.newValue === "light" || event.newValue === "dark")) {
        setTheme(event.newValue);
      }
    };

    const handleThemeChange = (event) => {
      const nextTheme = event?.detail?.theme;
      if (nextTheme === "light" || nextTheme === "dark") {
        setTheme(nextTheme);
      }
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(THEME_EVENT, handleThemeChange);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(THEME_EVENT, handleThemeChange);
    };
  }, []);

  useEffect(() => {
    if (!activeDesktopPanelItem) {
      setDesktopPanelPosition(null);
      return undefined;
    }

    let firstRafId = 0;
    let secondRafId = 0;

    const readCssLength = (value) => {
      if (typeof value !== "string") return 0;

      const normalized = value.trim();
      if (!normalized) return 0;

      if (normalized.endsWith("rem")) {
        const remValue = Number.parseFloat(normalized);
        const rootFontSize = Number.parseFloat(
          window.getComputedStyle(document.documentElement).fontSize || "16"
        );
        return Number.isFinite(remValue) && Number.isFinite(rootFontSize)
          ? remValue * rootFontSize
          : 0;
      }

      if (normalized.endsWith("px")) {
        const pxValue = Number.parseFloat(normalized);
        return Number.isFinite(pxValue) ? pxValue : 0;
      }

      const plainValue = Number.parseFloat(normalized);
      return Number.isFinite(plainValue) ? plainValue : 0;
    };

    const updateDesktopPanelPosition = () => {
      const triggerNode = triggerRefs.current[activeDesktopPanelItem.key];
      const navNode = navRef.current;

      if (!triggerNode || !navNode) {
        return;
      }

      const rect = triggerNode.getBoundingClientRect();
      const navRect = navNode.getBoundingClientRect();
      const expandedWidth =
        readCssLength(window.getComputedStyle(navNode).getPropertyValue("--nsb-expanded-w")) ||
        navRect.width ||
        rect.width;
      // The panel is positioned inside the sidebar, whose backdrop filter also
      // creates a containing block. Convert viewport measurements to local coordinates.
      const panelHeight = desktopPanelRef.current?.getBoundingClientRect().height || 140;
      const viewportTop = Math.max(navRect.top + 12, Math.min(rect.top, window.innerHeight - panelHeight - 12));
      const top = viewportTop - navRect.top - navNode.clientTop;
      const left = expandedWidth + 6 - navNode.clientLeft;

      setDesktopPanelPosition({ top, left });
    };

    updateDesktopPanelPosition();
    firstRafId = window.requestAnimationFrame(updateDesktopPanelPosition);
    secondRafId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(updateDesktopPanelPosition);
    });

    const navScrollNode = navRef.current?.querySelector(".New-Sidebar__navScroll");
    const navNode = navRef.current;
    window.addEventListener("resize", updateDesktopPanelPosition, { passive: true });
    navScrollNode?.addEventListener("scroll", updateDesktopPanelPosition, { passive: true });
    navNode?.addEventListener("transitionrun", updateDesktopPanelPosition);
    navNode?.addEventListener("transitionend", updateDesktopPanelPosition);

    return () => {
      window.cancelAnimationFrame(firstRafId);
      window.cancelAnimationFrame(secondRafId);
      window.removeEventListener("resize", updateDesktopPanelPosition);
      navScrollNode?.removeEventListener("scroll", updateDesktopPanelPosition);
      navNode?.removeEventListener("transitionrun", updateDesktopPanelPosition);
      navNode?.removeEventListener("transitionend", updateDesktopPanelPosition);
    };
  }, [activeDesktopPanelItem]);

  const teamQuota = quotaState.teamQuota;
  const quotaFallback = getPlanQuotaFallback(
    subscription?.planKey || teamQuota?.planKey || null,
    subscription?.selectedStorageAddOnKey || null,
  );
  const uploadUsed = Number(
    quotaState.effectiveMonthlyUploadUsedBytes ??
      (Number(teamQuota?.monthlyUploadUsedBytes || 0) + Number(teamQuota?.liveReservedUploadBytes || 0)),
  );
  const uploadLimit = Number(teamQuota?.monthlyUploadLimitBytes || quotaFallback?.monthlyUploadLimitBytes || 0);
  const storageUsed = Number(
    quotaState.effectiveStorageUsedBytes ??
      (Number(teamQuota?.storageUsedBytes || 0) + Number(teamQuota?.liveReservedStorageBytes || 0)),
  );
  const storageLimit = Number(teamQuota?.storageLimitBytes || quotaFallback?.storageLimitBytes || 0);
  const currentCycleEnd = subscription?.currentPeriodEnd || teamQuota?.currentPeriodEnd || null;
  const uploadResetLabel = formatMonthDay(currentCycleEnd);

  const subscriptionPlanLabel = formatPlanLabel(subscription?.planKey || teamQuota?.planKey || null);
  const subscriptionStatusLabel = subscription?.cancelAtPeriodEnd
    ? "Ends At Period Close"
    : subscription?.status
      ? formatStatusLabel(subscription.status)
      : teamQuota?.hasActiveEntitlement === false
        ? "Inactive"
        : activeTeamId
          ? "Status Unknown"
          : "Select A Team";

  const displayName = useMemo(() => {
    const direct = typeof username === "string" && username.trim() ? username.trim() : "";
    if (direct) return direct;

    const fromUserDoc =
      typeof userData?.username === "string" && userData.username.trim() ? userData.username.trim() : "";
    if (fromUserDoc) return fromUserDoc;

    return userData?.email || "Guest";
  }, [userData?.email, userData?.username, username]);

  const profileInitial = displayName.trim().charAt(0).toUpperCase() || "G";

  const handleTeamSwitch = async (event) => {
    const nextTeamId = event.target.value || null;

    if (!userData?.uid || teamSwitching) {
      return;
    }

    setTeamSwitching(true);
    try {
      await updateDoc(doc(db, "users", userData.uid), {
        activeTeamId: nextTeamId,
      });
      setTeamId(nextTeamId);
      if(location.pathname.startsWith("/team-stats")){const params=new URLSearchParams(location.search);params.delete("teamId");params.delete("match");navigate({pathname:location.pathname,search:params.size?"?"+params.toString():""},{replace:true});}
    } catch (error) {
      console.error("Sidebar team switch failed:", error);
      setAlertState({
        message: "Could not switch the active team.",
        type: "error",
        position: "top",
      });
    } finally {
      setTeamSwitching(false);
    }
  };

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const closePanels = () => {
    clearCloseTimer();
    setOpenDropdown(null);
  };

  const handleSidebarNavigation = () => {
    clearCloseTimer();
    setOpenDropdown(null);

    const activeElement = document.activeElement;
    if (
      activeElement &&
      typeof activeElement.blur === "function" &&
      navRef.current?.contains(activeElement)
    ) {
      activeElement.blur();
    }

    if (!isMobile) {
      setSuppressHoverExpand(true);
    }
  };

  const openWithCancel = (key) => {
    clearCloseTimer();
    setOpenDropdown(key);
  };

  const delayedClose = () => {
    if (isMobile) {
      return;
    }

    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpenDropdown(null);
      closeTimerRef.current = null;
    }, 120);
  };

  const handleDisabledClick = (message) => {
    setAlertState({
      message,
      type: "info",
      position: "top",
    });
  };

  const toggleTheme = () => {
    const nextTheme = theme === "dark" ? "light" : "dark";
    localStorage.setItem("theme", nextTheme);
    setTheme(nextTheme);
    broadcastTheme(nextTheme);
  };

  const handleTriggerClick = (item, event) => {
    if (!item.panel) {
      handleSidebarNavigation();
      return;
    }

    if (!isMobile) {
      setSuppressHoverExpand(true);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setOpenDropdown((current) => (current === item.key ? null : item.key));
  };

  const setTriggerRef = (key, node) => {
    if (node) {
      triggerRefs.current[key] = node;
      return;
    }

    delete triggerRefs.current[key];
  };

  return (
    <nav
      ref={navRef}
      className={`New-Sidebar ${isMobile ? "New-Sidebar--mobile" : ""} ${
        openDropdown ? "is-panel-open" : ""
      } ${suppressHoverExpand ? "is-hover-suppressed" : ""}`}
      aria-label="Primary sidebar navigation"
      onMouseLeave={() => {
        if (!isMobile && suppressHoverExpand) {
          const activeElement = document.activeElement;
          if (
            activeElement &&
            typeof activeElement.blur === "function" &&
            navRef.current?.contains(activeElement)
          ) {
            activeElement.blur();
          }

          setSuppressHoverExpand(false);
        }
      }}
    >
      {alertState ? (
        <CustomAlert
          message={alertState.message}
          type={alertState.type}
          position={alertState.position}
          onClose={() => setAlertState(null)}
        />
      ) : null}

      <div className="New-Sidebar__shell">

        <div className="New-Sidebar__navScroll">
          <ul className="New-Sidebar__list">
            {isMobile ? (
              <SidebarMobileAuthAction
                isLoggedIn={Boolean(userData?.uid)}
                isActive={isAuthRoute(location.pathname)}
                onNavigate={handleSidebarNavigation}
              />
            ) : null}

            {SIDEBAR_ITEMS.map((item) => {
              if (!isMobile && SIDEBAR_UTILITY_KEYS.has(item.key)) {
                return null;
              }

              const isActive = isItemActive(item, location.pathname, location.hash, location.search);
              const isOpen = openDropdown === item.key;
              const hasPanel = !!item.panel;
              const Icon = item.icon;

              return (
                <li
                  key={item.key}
                  data-key={item.key}
                  className={`New-Sidebar__item ${hasPanel ? "New-Sidebar__item--hasPanel" : ""} ${
                    isOpen ? "is-open" : ""
                  }`}
                  onMouseEnter={() => {
                    if (isMobile) {
                      return;
                    }

                    if (suppressHoverExpand) {
                      return;
                    }

                    if (hasPanel) {
                      openWithCancel(item.key);
                    } else {
                      closePanels();
                    }
                  }}
                  onMouseLeave={hasPanel && !isMobile ? delayedClose : undefined}
                >
                  <Link
                    to={item.to}
                    ref={(node) => setTriggerRef(item.key, node)}
                    className={`New-Sidebar__trigger ${isActive ? "is-active" : ""}`}
                    aria-haspopup={hasPanel ? "menu" : undefined}
                    aria-expanded={hasPanel ? isOpen : undefined}
                    onClick={(event) => handleTriggerClick(item, event)}
                    onFocus={() => {
                      if (!isMobile && hasPanel && !suppressHoverExpand) {
                        openWithCancel(item.key);
                      }
                    }}
                  >
                    <SidebarIcon icon={Icon} />
                    <span className="New-Sidebar__triggerLabel">{item.label}</span>
                    {hasPanel ? (
                      <ChevronRight className="New-Sidebar__triggerChevron" size={22} aria-hidden="true" />
                    ) : null}
                  </Link>

                  {hasPanel && isMobile ? (
                    <div className="New-Sidebar__panel" role="menu" hidden={!isOpen}>
                      <SidebarDropdownPanel
                        panel={item.panel}
                        isMobile={isMobile}
                        onNavigate={closePanels}
                        onDisabled={handleDisabledClick}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}


          </ul>


        </div>

          <section className="New-Sidebar__summary" aria-label="Active team summary">
            <SidebarSummaryCard
              activeTeamId={activeTeamId}
              authLoading={authLoading}
              userData={userData}
              teamOptions={teamOptions}
              handleTeamSwitch={handleTeamSwitch}
              teamSwitching={teamSwitching}
              closePanels={handleSidebarNavigation}
              subscriptionPlanLabel={subscriptionPlanLabel}
              subscriptionStatusLabel={subscriptionStatusLabel}
              uploadUsed={uploadUsed}
              uploadLimit={uploadLimit}
              uploadResetLabel={uploadResetLabel}
              storageUsed={storageUsed}
              storageLimit={storageLimit}
              quotaState={quotaState}
              showUsage={showQuotaUsage}
            />
          </section>

        {isMobile&&<button type="button" ref={node=>setTriggerRef(MOBILE_EXTRAS_KEY,node)} className="New-Sidebar__mobileTeamButton" aria-label="Open team usage and account" aria-expanded={openDropdown===MOBILE_EXTRAS_KEY} aria-controls="mobile-sidebar-extras" onClick={()=>{clearCloseTimer();setOpenDropdown(current=>current===MOBILE_EXTRAS_KEY?null:MOBILE_EXTRAS_KEY);}}>
          <strong>Team &amp; account</strong>
          {showQuotaUsage&&activeTeamId?<><span><Upload size={12}/>{formatGbValue(uploadUsed)} / {formatGbValue(uploadLimit)} GB</span><span><Cloud size={12}/>{formatGbValue(storageUsed)} / {formatGbValue(storageLimit)} GB</span>{Number(quotaState.userQuota?.egressLimitBytes)>0&&<span><Download size={12}/>{formatGbValue(quotaState.userQuota.egressUsedBytes||0)} / {formatGbValue(quotaState.userQuota.egressLimitBytes)} GB</span>}</>:<SidebarIcon icon={ICONS.settings}/>}
        </button>}
        {showQuotaUsage&&activeTeamId&&<section className="New-Sidebar__compactUsage" aria-label="Team and personal quota usage">
          <SidebarUsageCard compact label="Upload Limit" icon={Upload} usedBytes={uploadUsed} limitBytes={uploadLimit} resetLabel={uploadResetLabel} loading={quotaState.loading&&uploadLimit<=0}/>
          <SidebarUsageCard compact label="Cloud Storage" icon={Cloud} usedBytes={storageUsed} limitBytes={storageLimit} loading={quotaState.loading&&storageLimit<=0}/>
          <SidebarTransferUsage compact quotaState={quotaState} resetLabel={uploadResetLabel} />
        </section>}
        <footer className="New-Sidebar__footer">
          <SidebarFooterControls
            theme={theme}
            toggleTheme={toggleTheme}
            closePanels={handleSidebarNavigation}
            userData={userData}
            profileInitial={profileInitial}
            displayName={displayName}
            notificationTriggerMode="hover"
            notificationDropdownClassName="New-Sidebar__notificationDropdown"
            showProfile={false}
            compact
          />
          <SidebarUtilityDock
            closePanels={handleSidebarNavigation}
            userData={userData}
            profileInitial={profileInitial}
            displayName={displayName}
          />
        </footer>

        {isMobile ? (
          <div
            id="mobile-sidebar-extras"
            hidden={openDropdown !== MOBILE_EXTRAS_KEY}
            className={`New-Sidebar__mobileSurface ${openDropdown === MOBILE_EXTRAS_KEY ? "is-open" : ""}`}
            aria-hidden={openDropdown !== MOBILE_EXTRAS_KEY}
          >
            <div className="New-Sidebar__mobileSurfaceHeader">
              <strong>Team &amp; Account</strong>
              <button type="button" className="New-Sidebar__mobileSurfaceClose app-cancel-control" onClick={closePanels}>
                Close
              </button>
            </div>

            <SidebarSummaryCard
              activeTeamId={activeTeamId}
              authLoading={authLoading}
              userData={userData}
              teamOptions={teamOptions}
              handleTeamSwitch={handleTeamSwitch}
              teamSwitching={teamSwitching}
              closePanels={handleSidebarNavigation}
              subscriptionPlanLabel={subscriptionPlanLabel}
              subscriptionStatusLabel={subscriptionStatusLabel}
              uploadUsed={uploadUsed}
              uploadLimit={uploadLimit}
              uploadResetLabel={uploadResetLabel}
              storageUsed={storageUsed}
              storageLimit={storageLimit}
              quotaState={quotaState}
              showUsage={showQuotaUsage}
            />

            <div className="New-Sidebar__mobileSurfaceActions">
              <SidebarFooterControls
                theme={theme}
                toggleTheme={toggleTheme}
                closePanels={handleSidebarNavigation}
                userData={userData}
                profileInitial={profileInitial}
                displayName={displayName}
                notificationTriggerMode="click"
                notificationDropdownClassName="notif-dd--mobileCentered"
              />
            </div>
          </div>
        ) : null}
      </div>

      {activeDesktopPanelItem && desktopPanelPosition ? (
        <div
          ref={desktopPanelRef}
          className="New-Sidebar__panel New-Sidebar__panel--desktopOverlay"
          role="menu"
          style={desktopPanelPosition}
          onMouseEnter={() => openWithCancel(activeDesktopPanelItem.key)}
          onMouseLeave={delayedClose}
        >
          <SidebarDropdownPanel
            panel={activeDesktopPanelItem.panel}
            isMobile={false}
            onNavigate={handleSidebarNavigation}
            onDisabled={handleDisabledClick}
          />
        </div>
      ) : null}
    </nav>
  );
}

